import type { Config } from "wagmi"
import type { Address } from "viem"
import type { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { MinimalPoolRepository } from "@/domains/minimal-pool"
import type { ManualClPoolRepository as ThickV1PoolRepository } from "@/domains/thick-v1/manual-cl-repository"
import type { ManualClPoolRepository as ThickV2PoolRepository } from "@/domains/thick-v2/manual-cl-repository"
import type { TokenStore } from "@/domains/tokens"
import type { WalletConnectionStore } from "@/domains/wallet"
import { staticAggregators } from "@/domains/swap-aggregators/static-aggregators"

import type { ITokenGetAll, ITokenLookup } from "../swap/interfaces"
import type {
  ISwapAggregatorPlugin,
  ISwapAggregatorPluginRegistry,
  ISwapAggregatorSettingsStore,
  ISwapCoordinator,
  ISwapExecutionStore,
  ISwapInputsOrchestrator,
  ISwapInputStore,
  ISwapOrchestrator,
  ISwapQuoteStore,
} from "./interfaces"
import { FlySwapPlugin } from "./plugins/fly-plugin"
import { KyberSwapPlugin } from "./plugins/kyber-plugin"
import { OpenOceanSwapPlugin } from "./plugins/openocean-plugin"
import { ScaleSwapPlugin } from "./plugins/scale-plugin"
import { PriceImpactCalculator } from "./services/price-impact-calculator"
import { ScaleRouteFinder } from "./services/scale-route-finder"
import { SwapAggregatorPluginRegistry } from "./swap-aggregator-plugin-registry"
import { SwapCoordinator } from "./swap-coordinator"
import { SwapExecutionStore } from "./swap-execution-store"
import { SwapInputStore } from "./swap-input-store"
import { SwapInputsOrchestrator } from "./swap-inputs-orchestrator"
import { SwapOrchestrator } from "./swap-orchestrator"
import { SwapQuoteStore } from "./swap-quote-store"
import { ScaleRouterOnChainTransport } from "./transports/scale-router-on-chain-transport"

export type SwapNamespace = {
  QuoteStore: ISwapQuoteStore
  InputStore: ISwapInputStore
  ExecutionStore: ISwapExecutionStore
  Orchestrator: ISwapOrchestrator
  InputsOrchestrator: ISwapInputsOrchestrator
  PluginRegistry: ISwapAggregatorPluginRegistry
  Coordinator: ISwapCoordinator
}

export type SwapNamespaceFactoryParams = {
  chainId: number
  orchestratedQueryStore: OrchestratedQueryStore
  tokenRepository: ITokenLookup & ITokenGetAll
  storage: IStorageBackend
  urlParams: IUrlParams
  chainSlug: string
  nativeToken: TokenStore
  wrappedNativeToken: TokenStore
  walletStore: WalletConnectionStore
  eventBus: IEventBus
  config: Config
  routerAddress: Address
  getMinimalPoolRepository: () => MinimalPoolRepository | undefined
  getThickV1PoolRepository?: () => ThickV1PoolRepository | undefined
  getThickV2PoolRepository?: () => ThickV2PoolRepository | undefined
  settingsStore: ISwapAggregatorSettingsStore
  getNativeTokenPrice: () => number
  getSlippageBps: () => number
}

export const SwapNamespaceFactory = {
  create({
    chainId,
    orchestratedQueryStore,
    tokenRepository,
    storage,
    urlParams,
    chainSlug,
    nativeToken,
    wrappedNativeToken,
    walletStore,
    eventBus,
    config,
    routerAddress,
    getMinimalPoolRepository,
    getThickV1PoolRepository,
    getThickV2PoolRepository,
    settingsStore,
    getNativeTokenPrice,
    getSlippageBps,
  }: SwapNamespaceFactoryParams): SwapNamespace {
    // Create price impact calculator
    const priceImpactCalculator = new PriceImpactCalculator()

    // Create plugin registry
    const pluginRegistry = new SwapAggregatorPluginRegistry()

    const scaleRouteFinder = new ScaleRouteFinder({
      getPoolRepository: getMinimalPoolRepository,
      getThickV1PoolRepository,
      getThickV2PoolRepository,
      hubTokens: () =>
        tokenRepository
          .getAll()
          .filter((token) => token.isSwapToken || token.address.equals(wrappedNativeToken.address))
          .sort((a, b) => {
            const preferredSymbols = ["WETH", "SCALE", "USDC", "frxUSD", "USDbC"]
            const aIndex = preferredSymbols.indexOf(a.symbol)
            const bIndex = preferredSymbols.indexOf(b.symbol)
            const aRank = aIndex === -1 ? preferredSymbols.length : aIndex
            const bRank = bIndex === -1 ? preferredSymbols.length : bIndex
            return aRank - bRank
          })
          .slice(0, 8),
    })
    const scaleOnChainTransport = new ScaleRouterOnChainTransport(config, chainId, routerAddress)
    const scalePlugin = new ScaleSwapPlugin(
      scaleRouteFinder,
      scaleOnChainTransport,
      priceImpactCalculator,
      wrappedNativeToken,
      () => config,
    )
    pluginRegistry.register(scalePlugin)

    // Plugin factory map: aggregator ID -> plugin factory function
    type PluginFactory = (
      priceImpactCalculator: PriceImpactCalculator,
      config: Config,
      getNativeTokenPrice: () => number,
    ) => ISwapAggregatorPlugin

    const pluginFactories: Record<string, PluginFactory> = {
      fly: (priceImpactCalculator, config) => new FlySwapPlugin(priceImpactCalculator, () => config),
      kyber: (priceImpactCalculator, config) => new KyberSwapPlugin(priceImpactCalculator, () => config),
      openocean: (priceImpactCalculator, config, getNativeTokenPrice) =>
        new OpenOceanSwapPlugin(priceImpactCalculator, getNativeTokenPrice, () => config),
    }

    // Create and register plugins dynamically based on static aggregators config
    const enabledAggregators = staticAggregators.filter((agg) => agg.isProductionReady)
    const plugins: Array<{ id: string; plugin: ISwapAggregatorPlugin }> = [{ id: scalePlugin.id, plugin: scalePlugin }]

    for (const aggregator of enabledAggregators) {
      const factory = pluginFactories[aggregator.id]
      if (factory) {
        const plugin = factory(priceImpactCalculator, config, getNativeTokenPrice)
        pluginRegistry.register(plugin)
        plugins.push({ id: aggregator.id, plugin })
      }
    }

    // Create stores first (coordinator needs inputStore)
    const inputStore = new SwapInputStore(storage, urlParams, chainSlug, tokenRepository, nativeToken)
    inputStore.initialize()
    const executionStore = new SwapExecutionStore()

    // Create coordinator (manages pausing/resuming quote queries)
    // Coordinator reacts to events to coordinate quote queries
    const coordinator = new SwapCoordinator(orchestratedQueryStore, eventBus)

    // Create quote store (query layer - doesn't own quotes)
    const quoteStore = new SwapQuoteStore(pluginRegistry, settingsStore, eventBus, inputStore)

    // Create inputs orchestrator (manages quote request timing based on user interaction)
    const inputsOrchestrator = new SwapInputsOrchestrator(
      inputStore,
      quoteStore,
      eventBus,
      chainId,
      nativeToken,
      wrappedNativeToken,
      getSlippageBps,
    )

    // Create orchestrator (simplified - only coordinates execution)
    const orchestrator = new SwapOrchestrator(
      quoteStore,
      executionStore,
      pluginRegistry,
      eventBus,
      walletStore,
      settingsStore,
      nativeToken,
      wrappedNativeToken,
      inputStore,
    )

    // Get slippage
    const slippageBps = getSlippageBps()

    // Initialize plugins (each creates its own OrchestratedQuery)
    // Plugins will get user address dynamically from walletStore
    for (const { id, plugin } of plugins) {
      plugin.initialize(
        orchestratedQueryStore,
        eventBus,
        coordinator,
        inputStore,
        priceImpactCalculator,
        chainId,
        nativeToken,
        walletStore,
        slippageBps,
        settingsStore,
        undefined, // Use default ApiTransport (when implemented)
        undefined, // No on-chain transport yet
      )
    }

    return {
      QuoteStore: quoteStore,
      InputStore: inputStore,
      ExecutionStore: executionStore,
      Orchestrator: orchestrator,
      InputsOrchestrator: inputsOrchestrator,
      PluginRegistry: pluginRegistry,
      Coordinator: coordinator,
    }
  },
}
